import React from 'react';
import { ChevronRight } from 'lucide-react';
import { Checkbox } from '@cotera/client/v0/components/ui/checkbox';
import { cn } from '@cotera/client/v0/lib/utils';
import type { ItemAction } from './resource-list-view-types';
import { RowActionsMenu } from './row-actions-menu';

export type VirtualListItemProps<T> = {
  item: T;
  index: number;
  virtualStartY: number;
  optionId: string;
  renderItem: (item: T) => React.ReactNode;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  renderIcon?: (item: T) => React.ReactNode;
  useCustomIcon: boolean;
  hasItemActions: boolean;
  itemActions?: (item: T) => ItemAction[];
  onOpenActionMenu?: (item: T) => void;
  isSelected: boolean;
  /** The keyboard cursor is on this row (list is focused) — draws a focus ring. */
  isFocused: boolean;
  /** When true, the leading icon is replaced with a selection checkbox (legacy Shift-to-reveal mode). */
  multiSelectActive: boolean;
  /**
   * When true, a persistent selection checkbox is rendered in its own leading
   * column, ahead of the type icon (which keeps its normal icon / hover-actions
   * behavior). Overrides the icon-morphing `multiSelectActive` behavior.
   */
  showSelectColumn: boolean;
  /** Row renders the `⋯` menu in a trailing column rather than over the icon. */
  trailingActions: boolean;
  /** Row is a drag source (HTML5 draggable). */
  isDraggable?: boolean;
  /** Row is part of the currently-dragged set (dimmed). */
  isDragging?: boolean;
  /** Row is the hovered drop target (highlighted). */
  isDropTarget?: boolean;
  onDragStartRow?: (
    index: number,
    event: React.DragEvent<HTMLDivElement>
  ) => void;
  onDragOverRow?: (
    index: number,
    event: React.DragEvent<HTMLDivElement>
  ) => void;
  onDropRow?: (index: number, event: React.DragEvent<HTMLDivElement>) => void;
  onDragEndRow?: () => void;
  onRowClick: (
    item: T,
    index: number,
    event: React.MouseEvent<HTMLDivElement>
  ) => void;
  onToggleSelect?: (index: number) => void;
  /** Shift-click a checkbox to extend the selection as a range (like the row body). */
  onShiftSelect?: (index: number) => void;
  onMouseEnterRow: (index: number) => void;
  rowRef: (el: HTMLDivElement | null) => void;
  onClickItem?: (item: T) => void;
  /**
   * When set, the content cell is a real anchor to this URL so the row can be
   * opened in a new tab (⌘/ctrl-click, middle-click, context menu).
   */
  href?: string;
  /** Embedded/narrow lists use stacked rows instead of a multi-column table layout. */
  compactRows?: boolean;
  /**
   * `custom` hides the shared icon/kebab chrome so the row can render a full
   * card (e.g. workflow trigger accordion). Multi-select still shows a checkbox.
   */
  chrome?: 'default' | 'custom';
  /**
   * Tree position. When set, the row indents by depth and expandable rows get a
   * disclosure control ahead of the type icon.
   */
  treeMeta?: {
    depth: number;
    hasChildren: boolean;
    isExpanded: boolean;
    isLoadingChildren?: boolean;
  };
  onToggleExpand?: (item: T) => void;
};

/** Horizontal offset per tree level. */
const TREE_INDENT_PX = 20;

export function VirtualListItem<T>({
  item,
  index,
  virtualStartY,
  optionId,
  renderItem,
  icon: Icon,
  renderIcon,
  useCustomIcon,
  hasItemActions,
  itemActions,
  onOpenActionMenu,
  isSelected,
  isFocused,
  multiSelectActive,
  showSelectColumn,
  trailingActions,
  isDraggable = false,
  isDragging = false,
  isDropTarget = false,
  onDragStartRow,
  onDragOverRow,
  onDropRow,
  onDragEndRow,
  onRowClick,
  onToggleSelect,
  onShiftSelect,
  onMouseEnterRow,
  rowRef,
  onClickItem,
  href,
  compactRows = false,
  chrome = 'default',
  treeMeta,
  onToggleExpand,
}: VirtualListItemProps<T>): React.ReactElement {
  const rendered = renderItem(item);
  const cellContent =
    React.isValidElement(rendered) && rendered.type === 'td'
      ? (
          rendered as React.ReactElement<{
            children?: React.ReactNode;
          }>
        ).props.children
      : rendered;

  const actions =
    hasItemActions && itemActions !== undefined ? itemActions(item) : [];

  const selectionCheckbox = (
    <Checkbox
      checked={isSelected}
      aria-label={isSelected ? 'Deselect item' : 'Select item'}
      // Don't take focus on click — a focused checkbox under the sticky header
      // gets native-scrolled into view, which jumps the list.
      onMouseDown={(e) => {
        e.preventDefault();
      }}
      onClick={(e) => {
        e.stopPropagation();
        // Handle the mouse toggle ourselves so Shift-click can range-select.
        // preventDefault makes Radix skip its own toggle (onCheckedChange);
        // keyboard toggles (Space/Enter) still fall through to onCheckedChange.
        e.preventDefault();
        if (e.shiftKey && onShiftSelect !== undefined) {
          onShiftSelect(index);
        } else {
          onToggleSelect?.(index);
        }
      }}
      onCheckedChange={() => {
        onToggleSelect?.(index);
      }}
    />
  );

  // Legacy mode morphs the icon cell into a checkbox once a selection exists.
  // With a persistent select column the checkbox has its own slot, so the icon
  // cell always shows the type icon / hover actions.
  const iconIsCheckbox = !showSelectColumn && multiSelectActive;
  const showIconColumn = chrome === 'default' || iconIsCheckbox;
  const leadingColCount = showIconColumn ? 1 : 0;
  // Library keeps the same natural icon/content grid as every other list — the
  // selection controls live in the left margin (an absolutely-positioned span)
  // so they don't indent the icons away from the "Name" header / filter tabs.
  // 40px (not 28px) so the color-coded resource icon bubble (h-8) has room.
  // In tree mode a fixed 20px disclosure column sits ahead of the icon, so
  // chevrons and icons stay in their own aligned columns at every depth.
  const isTreeRow = treeMeta !== undefined;
  // The ⋯ menu gets a real trailing column rather than an overlay, so it can
  // never sit on top of the last cell's content. It lives on the right (where
  // row actions live in Drive/Linear/GitHub) instead of the left margin, which
  // the page gutter can clip.
  // The list decides whether the column exists; a row still opts out when it has
  // no actions of its own to put in it.
  const showTrailingActions = trailingActions && actions.length > 0;
  const gridClass = showIconColumn
    ? cn(
        'group grid table-fixed',
        isTreeRow
          ? showTrailingActions
            ? 'grid-cols-[20px_40px_1fr_44px]'
            : 'grid-cols-[20px_40px_1fr]'
          : showTrailingActions
          ? 'grid-cols-[40px_1fr_44px]'
          : 'grid-cols-[40px_1fr]'
      )
    : 'group block';

  const leadingIcon = iconIsCheckbox ? (
    selectionCheckbox
  ) : useCustomIcon ? (
    renderIcon?.(item)
  ) : (
    <Icon className="h-5 w-5 text-muted-foreground" />
  );

  // In persistent-select (Library) mode the checkbox is quiet by default and
  // only reveals on row hover — until a selection exists, at which point every
  // row keeps its checkbox visible so the multi-select is easy to build on.
  const selectionPersistent = multiSelectActive || isSelected;
  // Hidden controls must still be reachable by keyboard, so every reveal is
  // `focus-within` as well as `group-hover` — otherwise tabbing through the list
  // skips the checkbox and ⋯ menu entirely (they were `pointer-events-none` with
  // no focus escape hatch). `has-[:focus-visible]` keeps the reveal tied to
  // keyboard focus rather than firing on every mouse click.
  const KEYBOARD_REVEAL =
    'group-hover:opacity-100 group-hover:pointer-events-auto focus-within:opacity-100 focus-within:pointer-events-auto has-[:focus-visible]:opacity-100 has-[:focus-visible]:pointer-events-auto';
  // The checkbox reveals on hover/focus but stays put once a selection is active
  // (so the whole library reads as "in multi-select"). `pointer-events-none`
  // while hidden so the invisible control doesn't swallow clicks meant to open
  // the row.
  const checkboxRevealClass = selectionPersistent
    ? 'opacity-100'
    : cn('opacity-0 pointer-events-none transition-opacity', KEYBOARD_REVEAL);
  // The ⋯ menu stays quiet by default regardless of selection — it's a per-row
  // action, not part of the multi-select affordance — and reveals on hover or
  // keyboard focus. On a coarse pointer there IS no hover, so it stays visible
  // there: a hover-only control is simply unreachable on touch.
  const actionsRevealClass = cn(
    'opacity-0 pointer-events-none transition-opacity',
    KEYBOARD_REVEAL,
    '[@media(pointer:coarse)]:opacity-100 [@media(pointer:coarse)]:pointer-events-auto'
  );

  return (
    <div
      id={optionId}
      // `row` inside the list's `grid`, not `option` inside a `listbox`: rows
      // own interactive descendants (the selection checkbox and the ⋯ menu),
      // which the `option` role forbids — screen readers would not expose them.
      role="row"
      aria-selected={isSelected}
      data-selected={isSelected ? 'true' : undefined}
      data-focused={isFocused ? 'true' : undefined}
      data-drop-target={isDropTarget ? 'true' : undefined}
      ref={rowRef}
      data-index={index}
      draggable={isDraggable}
      aria-level={treeMeta !== undefined ? treeMeta.depth + 1 : undefined}
      aria-expanded={
        treeMeta !== undefined && treeMeta.hasChildren
          ? treeMeta.isExpanded
          : undefined
      }
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        transform: `translateY(${virtualStartY}px)`,
        opacity: isDragging ? 0.4 : undefined,
        // Indent the whole row (icon included), so depth reads the way it does
        // in any file tree rather than only shifting the label.
        paddingLeft:
          treeMeta !== undefined && treeMeta.depth > 0
            ? `${treeMeta.depth * TREE_INDENT_PX}px`
            : undefined,
      }}
      className={cn(
        'min-w-0 pb-2',
        gridClass,
        chrome === 'default' &&
          '[&>div]:transition-colors [&>div]:duration-150',
        // Cell backgrounds on hover / selected / keyboard-focus.
        chrome === 'default' &&
          '[&>div]:group-hover:bg-secondary [&[data-selected=true]>div]:bg-secondary [&[data-focused=true]>div]:bg-secondary',
        // Right rounding on the last cell (always).
        chrome === 'default' &&
          '[&>div:last-child]:group-hover:rounded-r-md [&[data-selected=true]>div:last-child]:rounded-r-md [&[data-focused=true]>div:last-child]:rounded-r-md',
        // Left rounding on the first (icon) cell. `first-of-type` (not
        // `first-child`) because the Library controls span is the first DOM child
        // — a plain `div:first-child` would match nothing and leave a square edge.
        chrome === 'default' &&
          '[&>div:first-of-type]:group-hover:rounded-l-md [&[data-selected=true]>div:first-of-type]:rounded-l-md [&[data-focused=true]>div:first-of-type]:rounded-l-md',
        // Drop target (drag-and-drop): tint the row so it clearly reads as
        // "drop here", overriding the hover treatment underneath.
        chrome === 'default' &&
          '[&[data-drop-target=true]>div]:!bg-primary/15 [&[data-drop-target=true]>div:first-of-type]:rounded-l-md [&[data-drop-target=true]>div:last-child]:rounded-r-md',
        chrome === 'default' &&
          !showSelectColumn &&
          '[&:has(button:hover)>div]:!bg-transparent [&:has(button[data-state=open])>div]:!bg-transparent',
        onClickItem !== undefined && 'cursor-pointer',
        // Grab affordance so draggable rows advertise themselves; without it
        // drag-and-drop was invisible until someone happened to try it.
        isDraggable && 'cursor-grab active:cursor-grabbing'
      )}
      onMouseDown={(e) => {
        // Shift+click range-selects; prevent the browser selecting row text.
        if (e.shiftKey) {
          e.preventDefault();
        }
      }}
      onClick={(e) => onRowClick(item, index, e)}
      onMouseEnter={() => {
        onMouseEnterRow(index);
      }}
      onDragStart={
        onDragStartRow !== undefined
          ? (e) => onDragStartRow(index, e)
          : undefined
      }
      onDragOver={
        onDragOverRow !== undefined ? (e) => onDragOverRow(index, e) : undefined
      }
      onDrop={onDropRow !== undefined ? (e) => onDropRow(index, e) : undefined}
      onDragEnd={onDragEndRow}
    >
      {showSelectColumn ? (
        // The checkbox floats in the left margin (outside the icon/content grid)
        // so the icons stay aligned with the "Name" header and filter tabs, and
        // the row highlight stays within the grid. The ⋯ menu used to sit here
        // too, but the left gutter is narrower than the pair needs, so it moved
        // to its own trailing column at the end of the row.
        //
        // These absolute offsets resolve against the row wrapper above, which is
        // the positioning context: it's `position: absolute` (set inline for the
        // virtualizer), so right-full/top/bottom anchor to each row individually.
        <span className="absolute right-full top-0 bottom-2 flex items-center pr-2">
          <span className={cn('inline-flex', checkboxRevealClass)}>
            {selectionCheckbox}
          </span>
        </span>
      ) : null}
      {isTreeRow && showIconColumn ? (
        <div
          role="gridcell"
          className={cn(
            'flex items-start justify-center @list-cols/list:items-center',
            chrome === 'default' ? 'py-3 border-b border-border/40' : 'py-3'
          )}
        >
          {treeMeta.hasChildren ? (
            <button
              type="button"
              // Toggling must not also open the row, and must not disturb the
              // selection — expansion is navigation, not selection.
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand?.(item);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              aria-label={treeMeta.isExpanded ? 'Collapse' : 'Expand'}
              className="inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground cursor-pointer @list-cols/list:mt-0 mt-3"
            >
              <ChevronRight
                className={cn(
                  'h-3.5 w-3.5 transition-transform',
                  treeMeta.isExpanded && 'rotate-90',
                  treeMeta.isLoadingChildren === true && 'animate-pulse'
                )}
                aria-hidden="true"
              />
            </button>
          ) : null}
        </div>
      ) : null}
      {showIconColumn ? (
        <div
          role="gridcell"
          className={cn(
            'group/icon flex items-start justify-center @list-cols/list:items-center',
            chrome === 'default'
              ? 'py-3 pl-2 pr-0 border-b border-border/40'
              : 'py-3 pl-2 pr-0'
          )}
        >
          {showSelectColumn || showTrailingActions ? (
            // The type icon stays put whenever the ⋯ menu has a column of its
            // own (and, in Library mode, the checkbox has one too).
            <span className="inline-flex items-center justify-center h-8 w-8 @list-cols/list:mt-0 mt-3">
              {leadingIcon}
            </span>
          ) : chrome === 'default' && actions.length > 0 ? (
            <span className="relative inline-flex items-center justify-center h-8 w-8">
              <span
                className={cn(
                  'transition-opacity @list-cols/list:mt-0 mt-3',
                  iconIsCheckbox ? '' : 'group-hover/icon:opacity-0'
                )}
              >
                {leadingIcon}
              </span>
              {iconIsCheckbox ? null : (
                <span className="absolute inset-0 opacity-0 group-hover/icon:opacity-100 has-[button[data-state=open]]:opacity-100 transition-opacity flex items-center justify-center">
                  <RowActionsMenu
                    actions={actions}
                    onOpen={() => onOpenActionMenu?.(item)}
                    align="start"
                    contentClassName="w-40"
                  />
                </span>
              )}
            </span>
          ) : (
            leadingIcon
          )}
        </div>
      ) : null}
      <ContentCell
        href={href}
        // A dragged link carries its URL; on a draggable row that would hijack
        // the row's own drag-and-drop.
        draggable={isDraggable ? false : undefined}
        className={cn(
          'min-w-0 w-full',
          chrome === 'default' &&
            'py-3 pl-4 pr-3 border-b border-border/40 flex flex-col gap-1.5 items-stretch',
          // `pr-0` is what makes a header sit over its own column.
          //
          // The header is a `table-fixed` with a colgroup, so its columns end
          // at the row's right edge. The row's secondary cells are anchored to
          // the right too (the name is the only `flex-1`), but from *inside*
          // this cell's padding — so `pr-3` shifted every one of them left of
          // the header above it by exactly that gutter. Right-aligned values
          // hid it; left-aligned it is plainly visible, as each label sitting a
          // gutter to the right of the values it names. Stacked rows keep the
          // padding: they have no header to line up with.
          chrome === 'default' &&
            !compactRows &&
            '@list-cols/list:flex-row @list-cols/list:items-center @list-cols/list:gap-0 @list-cols/list:pr-0',
          chrome === 'custom' && leadingColCount > 0 && 'pl-2'
        )}
      >
        {cellContent}
      </ContentCell>
      {showTrailingActions ? (
        <div
          role="gridcell"
          className={cn(
            'flex items-start justify-end @list-cols/list:items-center',
            chrome === 'default'
              ? 'py-3 pr-2 border-b border-border/40'
              : 'py-3 pr-2'
          )}
        >
          <span
            className={cn(
              'inline-flex @list-cols/list:mt-0 mt-3',
              actionsRevealClass,
              // Stay visible while the dropdown is open, even once the pointer
              // has left the row to reach the menu.
              'has-[button[data-state=open]]:opacity-100 has-[button[data-state=open]]:pointer-events-auto'
            )}
          >
            <RowActionsMenu
              actions={actions}
              onOpen={() => onOpenActionMenu?.(item)}
              align="end"
              contentClassName="w-40"
            />
          </span>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The row's main cell. With an `href` its content is wrapped in an anchor, so
 * the browser's own new-tab gestures work: ⌘/ctrl-click opens a tab (and must
 * not also navigate this one, so it stops before the row's click handler),
 * middle-click and the context menu never reach `onClick` at all. Every other
 * click is the row's to handle — open via the router, Shift range-select — so
 * the browser's default (full page load, Shift → new window) is suppressed.
 *
 * The anchor is `display: contents` and the cell stays a `div`: the row's hover
 * and selection styling targets its `div` children, and the content keeps
 * laying out against the cell exactly as it does without a link.
 */
function ContentCell({
  href,
  draggable,
  className,
  children,
}: {
  href: string | undefined;
  draggable: boolean | undefined;
  className: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div role="gridcell" className={className}>
      {href === undefined ? (
        children
      ) : (
        <a
          href={href}
          className="contents"
          // The list owns keyboard focus (one tab stop, arrow keys move the
          // cursor); a tab stop per row would break that.
          tabIndex={-1}
          draggable={draggable}
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey) {
              e.stopPropagation();
              return;
            }
            e.preventDefault();
          }}
        >
          {children}
        </a>
      )}
    </div>
  );
}
